import { useSyncExternalStore } from 'react';

/**
 * The smallest observable value that React can subscribe to.
 *
 * The portal has exactly two pieces of genuinely global mutable state — the signed-in
 * account and the in-flight request count — and both are written from outside React
 * (the api client, a storage event). A store library would be four dependencies to
 * hold two variables; `useSyncExternalStore` is the platform's own answer and needs
 * none.
 */
export interface Store<T> {
  get(): T;
  set(value: T): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();

  return {
    get: () => value,
    set(next: T) {
      if (Object.is(next, value)) return;
      value = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function useStore<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}
